/**
 * Paystack Inline (NGN) + demo stub when VITE_PAYSTACK_PUBLIC_KEY is unset.
 * Public key only — never put the secret key in the frontend or git.
 */
import type { PaymentMode, PaymentStatus } from '../types';

declare global {
  interface Window {
    PaystackPop?: new () => {
      newTransaction: (opts: Record<string, unknown>) => void;
    };
  }
}

export type ChargeRequest = {
  email: string;
  amountNgn: number;
  reference: string;
  metadata?: Record<string, string | number | boolean>;
};

export type ChargeResult = {
  ok: boolean;
  mode: PaymentMode;
  reference: string;
  cancelled?: boolean;
  message?: string;
};

const SCRIPT_URL = 'https://js.paystack.co/v2/inline.js';

let scriptPromise: Promise<void> | null = null;

export function getPaystackPublicKey(): string | null {
  const raw = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY;
  const key = typeof raw === 'string' ? raw.trim() : '';
  return key || null;
}

export function isPaystackConfigured(): boolean {
  return Boolean(getPaystackPublicKey());
}

export function paymentModeLabel(): string {
  return isPaystackConfigured() ? 'Paystack (NGN)' : 'Demo payment (no key)';
}

/** Paystack expects amount in kobo (NGN × 100). */
export function ngnToKobo(amountNgn: number): number {
  const n = Math.round(Number(amountNgn) || 0);
  return Math.max(0, n) * 100;
}

function loadPaystackScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Paystack requires a browser'));
  }
  if (window.PaystackPop) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Paystack Inline')));
      if (window.PaystackPop) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Paystack Inline'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Open Paystack Inline for NGN. When the public key is missing, returns
 * mode "demo" with ok:false so the UI can offer a Simulate payment control.
 */
export async function chargeWithPaystack(req: ChargeRequest): Promise<ChargeResult> {
  const key = getPaystackPublicKey();
  const reference = String(req.reference || `FL-${Date.now()}`);
  const amountNgn = Math.round(Number(req.amountNgn) || 0);

  if (!key) {
    return {
      ok: false,
      mode: 'demo',
      reference,
      message: 'Paystack public key not configured — use demo payment',
    };
  }

  if (!req.email || amountNgn < 1) {
    return {
      ok: false,
      mode: 'paystack',
      reference,
      message: 'Valid email and amount are required for Paystack',
    };
  }

  try {
    await loadPaystackScript();
  } catch (err) {
    return {
      ok: false,
      mode: 'paystack',
      reference,
      message: err instanceof Error ? err.message : 'Could not load Paystack',
    };
  }

  if (!window.PaystackPop) {
    return {
      ok: false,
      mode: 'paystack',
      reference,
      message: 'Paystack Inline failed to initialise',
    };
  }

  return new Promise((resolve) => {
    try {
      const popup = new window.PaystackPop!();
      popup.newTransaction({
        key,
        email: req.email.trim(),
        amount: ngnToKobo(amountNgn),
        currency: 'NGN',
        ref: reference,
        reference,
        metadata: {
          custom_fields: [
            {
              display_name: 'Order',
              variable_name: 'order_id',
              value: reference,
            },
          ],
          ...(req.metadata || {}),
        },
        onSuccess: (transaction: { reference?: string }) => {
          resolve({
            ok: true,
            mode: 'paystack',
            reference: String(transaction?.reference || reference),
          });
        },
        onCancel: () => {
          resolve({
            ok: false,
            mode: 'paystack',
            reference,
            cancelled: true,
            message: 'Payment cancelled',
          });
        },
      });
    } catch (err) {
      resolve({
        ok: false,
        mode: 'paystack',
        reference,
        message: err instanceof Error ? err.message : 'Paystack error',
      });
    }
  });
}

/** Mark a demo / stub payment as paid (no network). */
export function completeDemoPayment(reference: string): ChargeResult {
  return {
    ok: true,
    mode: 'demo',
    reference: reference.startsWith('DEMO-') ? reference : `DEMO-${reference}`,
  };
}

export function paymentStatusLabel(status: PaymentStatus | string | undefined): string {
  switch (status) {
    case 'paid':
      return 'Paid';
    case 'failed':
      return 'Payment failed';
    case 'cancelled':
      return 'Payment cancelled';
    case 'pending':
    default:
      return 'Awaiting payment';
  }
}
